// a react ionic page component to display the developer tools

import {
    IonBackButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonIcon,
    IonItem,
    IonLabel,
    IonList,
    IonPage,
    IonText,
    IonTitle,
    IonToolbar,
    useIonRouter,
} from "@ionic/react";
import { routes } from "../../route/constants";
import React, { useState } from "react";
import {
    bug,
    cardOutline,
    cog,
    flower,
    save,
    star,
    videocam,
    watch,
} from "ionicons/icons";
import { useTranslation } from "react-i18next";
import { isIos } from "../../services/platformDetection";
import { useSubscription } from "../../components/hooks";
import { isUserPlusEligible } from "../../services/subscriptionService";
import * as Sentry from "@sentry/react";
import { MigrateSystemBModal } from "../../modals/migrateSystemBModals";
import { triggerRatingPrompt } from "../../services/ratingPromptService";
import { getUXCamStatus, UXCamStatus } from "../../utils/UXCam";

const DeveloperTools: React.FC = () => {
    const { t } = useTranslation();
    const route = useIonRouter();
    const subscription = useSubscription();

    const [isOpen, setIsOpen] = useState(false);

    // REFSIX-776: lets a manual tester confirm on-device that session replay is
    // actually running (and grab the replay link to paste into the ticket)
    // rather than waiting to see whether a session shows up in the UXCam
    // dashboard afterwards. Reachable while logged out too — the auth stack
    // routes this page as well.
    const [uxCam, setUxCam] = useState<UXCamStatus>();
    const [uxCamChecking, setUxCamChecking] = useState(false);
    const checkUXCam = async () => {
        setUxCamChecking(true);
        setUxCam(await getUXCamStatus());
        setUxCamChecking(false);
    };

    const uxCamSummary = () => {
        if (uxCamChecking) return "checking…";
        if (!uxCam) return "tap to check";
        if (!uxCam.available) return "plugin not available (web build)";
        // Say when it's the local stand-in — "recording: YES" from the mock
        // proves our call sequence, not that UXCam has the session.
        const source = uxCam.mock ? "MOCK — " : "";
        return `${source}recording: ${uxCam.recording ? "YES" : "NO"} — ${
            uxCam.sessionUrl || "no session URL yet"
        }`;
    };

    return (
        <IonPage>
            <IonHeader>
                <IonToolbar>
                    <IonButtons slot="start">
                        <IonBackButton
                            defaultHref="/"
                            text={t("help.OnboardingVideo.controls.back")}
                        />
                    </IonButtons>
                    <IonTitle>Developer Tools</IonTitle>
                </IonToolbar>
            </IonHeader>
            <IonContent id="printme">
                <IonList>
                    <IonItem>
                        <IonText>
                            This page contains tools designed to help debug the
                            app.
                        </IonText>
                    </IonItem>
                    <IonItem routerLink={routes.mockWatchMessaging}>
                        <IonIcon icon={watch} slot="start" />
                        <IonLabel>Mock Watch</IonLabel>
                    </IonItem>
                    <IonItem routerLink={routes.refsixMoreWatchDebug}>
                        <IonIcon icon={watch} slot="start" />
                        <IonLabel>Watch Debugger</IonLabel>
                    </IonItem>
                    <IonItem routerLink={routes.pouchDebug}>
                        <IonIcon icon={save} slot="start" />
                        <IonLabel>PouchDB Changes</IonLabel>
                    </IonItem>
                    <IonItem routerLink={routes.pouchDebugMatch}>
                        <IonIcon icon={save} slot="start" />
                        <IonLabel>PouchDB Doc Revisions</IonLabel>
                    </IonItem>
                    <IonItem onClick={() => route.push(routes.registerUpgrade)}>
                        <IonIcon icon={cardOutline} slot="start" />
                        <IonLabel>Register upgrade flow</IonLabel>
                    </IonItem>
                    <IonItem
                        data-testid="developer-tools-onboarding-flow-button"
                        onClick={() =>
                            route.push(routes.refsixOnboardingGettingStarted)
                        }
                    >
                        <IonIcon icon={flower} slot="start" />
                        <IonLabel>OnBoarding flow</IonLabel>
                    </IonItem>
                    <IonItem
                        data-testid="developer-tools-onboarding-bt-flow-button"
                        onClick={() =>
                            route.push(routes.refsixOnBoardingShareLocationData)
                        }
                    >
                        <IonIcon icon={flower} slot="start" />
                        <IonLabel>OnBoarding Permissions</IonLabel>
                    </IonItem>
                    <IonItem
                        data-testid="developer-tools-onboarding-bt-flow-button"
                        onClick={() =>
                            route.push(
                                routes.refsixOnboardingFoundWatch +
                                    (isIos() ? "/apple" : "/google")
                            )
                        }
                    >
                        <IonIcon icon={flower} slot="start" />
                        <IonLabel>OnBoarding Bluetooth Watch</IonLabel>
                    </IonItem>
                    <IonItem
                        data-testid="developer-tools-upgrade-button"
                        onClick={() =>
                            route.push(
                                `${routes.upgrade}/${
                                    subscription?.isPlus ||
                                    !isUserPlusEligible()
                                        ? "pro"
                                        : "plus"
                                }`
                            )
                        }
                    >
                        <IonIcon icon={cardOutline} slot="start" />
                        <IonLabel>REFSIX PLUS UPGRADE</IonLabel>
                    </IonItem>
                    <IonItem
                        data-testid="developer-tools-crash"
                        onClick={() =>
                            Sentry.captureMessage(
                                "TEST: Developer tools crash button pressed"
                            )
                        }
                    >
                        <IonIcon icon={bug} slot="start" />
                        <IonLabel>Report crash</IonLabel>
                    </IonItem>
                    <IonItem
                        data-testid="developer-tools-migrateSinBinSystemB"
                        onClick={() => {
                            console.log("TEST: Migrate System B SinBin System");
                            setIsOpen(true);
                        }}
                    >
                        <IonIcon icon={cog} slot="start" />
                        <IonLabel>Migrate System B SinBin System</IonLabel>
                        <MigrateSystemBModal
                            isOpen={isOpen}
                            onDismiss={() => setIsOpen(false)}
                        />
                    </IonItem>
                    <IonItem
                        data-testid="developer-tools-crash"
                        onClick={async () => {
                            route.push(routes.review);
                        }}
                    >
                        {/*<IonIcon icon={} slot="start" />*/}
                        <IonLabel>Review</IonLabel>
                    </IonItem>
                    <IonItem
                        data-testid="developer-tools-uxcam-status"
                        onClick={() =>
                            checkUXCam().catch((err) =>
                                console.warn("UXCam status check failed", err)
                            )
                        }
                    >
                        <IonIcon icon={videocam} slot="start" />
                        <IonLabel className="ion-text-wrap">
                            <h2>UXCam session</h2>
                            <p data-testid="developer-tools-uxcam-summary">
                                {uxCamSummary()}
                            </p>
                        </IonLabel>
                    </IonItem>
                    <IonItem
                        data-testid="developer-tools-trigger-rating"
                        onClick={() =>
                            triggerRatingPrompt(false).catch((err) =>
                                console.warn("ratingPrompt trigger failed", err)
                            )
                        }
                    >
                        <IonIcon icon={star} slot="start" />
                        <IonLabel>Trigger Rating Prompt</IonLabel>
                    </IonItem>
                </IonList>
            </IonContent>
        </IonPage>
    );
};

export default DeveloperTools;
